import React, { useEffect, useState, useRef } from "react";
import { Button, Spinner, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Icon } from "@iconify/react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import EditAttendanceModal from "./EditAttendanceModal";
import moment from "moment";
import { API_URL } from "../..";

const TimesheetDailyView = ({ date, employee }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const tableRef = useRef();

  // Fetch daily attendance
  const fetchDaily = async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      query.append("date", moment(date).format("YYYY-MM-DD"));
      if (employee) query.append("employeeId", employee);

      const res = await fetch(
        `${API_URL}/api/attendance/timesheet-log/daily?${query.toString()}`
      );
      const json = await res.json();
      if (json.success) setData(json.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDaily();
  }, [date, employee]);

  // Handle update without reload
  const handleRecordUpdated = (updatedRecord) => {
    setData((prev) =>
      prev.map((item) => {
        // Match user and update their attendance
        if (item.user?.id === updatedRecord.user_id) {
          return {
            ...item,
            attendance: updatedRecord, // ✅ Replace or add attendance
          };
        }
        return item;
      })
    );
  };
  
  // Export as PDF
  const handleExportPDF = async () => {
    const input = tableRef.current;
    const pdf = new jsPDF("l", "pt", "a4");
    const canvas = await html2canvas(input, { scale: 1.3 });
    const imgData = canvas.toDataURL("image/png");
    const imgWidth = 800;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.setFontSize(18);
    pdf.text(`Daily Timesheet - ${moment(date).format("DD MMM YYYY")}`, 40, 30);
    pdf.addImage(imgData, "PNG", 40, 50, imgWidth, imgHeight);
    pdf.save(`Timesheet_${moment(date).format("YYYYMMDD")}.pdf`);
  };

  const formatTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm A") : "--");

  const getStatusBadge = (status) => {
    switch (status) {
      case "late":
        return "bg-warning text-dark";
      case "early":
      case "on-time":
        return "bg-success";
      case "absent":
        return "bg-danger";
      case "schedule off":
        return "bg-secondary-subtle text-muted";
      default:
        return "bg-light text-dark";
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Daily Timesheet ({moment(date).format("DD MMM YYYY")})</h5>
        <Button variant="outline-primary" onClick={handleExportPDF}>
          <i className="bi bi-file-earmark-pdf me-2"></i>Download PDF
        </Button>
      </div>

      <div className="card-body p-3">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Loading...
          </div>
        ) : (
          <div ref={tableRef}>
            <Table striped bordered hover responsive className="align-middle text-center">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Punch In</th>
                  <th>Punch Out</th>
                  <th>Total Hours</th>
                  <th>Break Time</th>
                  <th>Status</th>
                  <th>Overtime</th>
                  <th className="no-print">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((item, idx) => {
                    const att = item.attendance || {};
                    const overtime = att.attendanceOvertimes?.[0] || att.overtime;

                    return (
                      <tr key={item.user.id}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{item.user?.name || "N/A"}</td>
                        <td>{formatTime(att.punch_in_time)}</td>
                        <td>{formatTime(att.punch_out_time)}</td>
                        <td>{att.total_work_hours || "0"} hrs</td>
                        <td>{att.total_break_hours || "0"} hrs</td>
                        <td>
                          <span className={`badge ${getStatusBadge(att.status)}`}>
                            {att.status === 'on-time'? 'Present' : att.status?.toUpperCase()  || "N/A"}
                          </span>
                        </td>
                        <td>
                          {overtime ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  <div>
                                    <strong>Date:</strong>{" "}
                                    {moment(overtime.date || att.attendance_date).format("DD MMM")}
                                    <br />
                                    <span><strong>Overtime:</strong>{" "}{moment(overtime.start_time ,"HH:mm:ss").format("hh:mm A")}–{moment(overtime.end_time, "HH:mm:ss").format("hh:mm A")}</span>
                                    <span><strong>Overtime Duration:</strong>{" "}{overtime.total_duration}</span>
                                  
                                    <br />
                                    {overtime.description && (
                                      <>
                                        <strong>Desc:</strong> {overtime.description}
                                      </>
                                    )}
                                  </div>
                                </Tooltip>
                              }
                            >
                              <span className="text-success fw-bold">
                                <i className="bi bi-clock-history"></i> Overtime
                              </span>
                            </OverlayTrigger>
                          ) : (
                            "--"
                          )}
                        </td>
                        <td className="no-print">
                                <Button
                                    size="sm"
                                    variant="outline-dark"
                                    onClick={() => {
                                    // ✅ Include user_id with the attendance record
                                    setEditRecord({
                                        ...att,
                                        user_id: item.user?.id, 
                                    });
                                    }}
                                    disabled={!item.user?.id}
                                    title={!att.id ? "Add or edit attendance" : "Edit attendance"}
                                >
                                    <Icon icon="simple-line-icons:pencil" />
                                </Button>
                                </td>

                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center text-muted py-3">
                      No records found for {moment(date).format("DD MMM YYYY")}.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* 🔹 Edit Modal */}
      {editRecord && (
        <EditAttendanceModal
          
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onUpdated={(updatedRecord) => {
            handleRecordUpdated(updatedRecord);
            setEditRecord(null);
          }}
        />
      )}
    </div>
  );
};

export default TimesheetDailyView;
